/** Foodpanda plugin: status and settings, outlets, orders and their actions, the simulator. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';
import { api, ApiError, unwrap, unwrapJson, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';

export type FoodpandaStatus = Schemas['FoodpandaStatus'];
export type FoodpandaOrder = Schemas['FoodpandaOrder'];
export type FoodpandaOutlet = Schemas['FoodpandaOutlet'];
export type FoodpandaEvent = Schemas['FoodpandaEvent'];
export type UpdateFoodpanda = Schemas['UpdateFoodpandaRequest'];
export type RejectReason = Schemas['RejectFoodpandaOrderRequest']['reason'];
export type OrderStatus = FoodpandaOrder['status'];
export type FoodpandaMenu = Schemas['FoodpandaMenu'];
export type FoodpandaMenuItem = Schemas['FoodpandaMenuItem'];
export type FoodpandaPayout = Schemas['FoodpandaPayout'];
export type PayoutPreview = Schemas['FoodpandaPayoutPreview'];
export type PayoutColumns = PayoutPreview['columns'];

export const foodpandaKeys = {
  status: ['foodpanda'] as const,
  orders: (q: object) => ['foodpanda', 'orders', q] as const,
  events: (orderId?: string) => ['foodpanda', 'events', orderId ?? null] as const,
  menu: ['foodpanda', 'menu'] as const,
  payouts: ['foodpanda', 'payouts'] as const,
  payout: (id: string) => ['foodpanda', 'payouts', id] as const,
  report: (kind: string, range: object) => ['foodpanda', 'report', kind, range] as const,
};

export function useFoodpandaStatus(enabled = true) {
  return useQuery({
    queryKey: foodpandaKeys.status,
    queryFn: () => unwrap(api.GET('/foodpanda')),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

const isOffline = (error: unknown) => error instanceof ApiError && error.code === 'OFFLINE';

/**
 * The desktop app without its server answers 503 OFFLINE. Foodpanda works online only, so the page
 * says so while any Foodpanda query last failed that way (a later success clears it).
 */
export function useFoodpandaOffline() {
  const qc = useQueryClient();
  const cache = qc.getQueryCache();
  const subscribe = useCallback((cb: () => void) => cache.subscribe(cb), [cache]);
  return useSyncExternalStore(subscribe, () =>
    cache
      .findAll({ queryKey: ['foodpanda'] })
      .some((q) => q.state.status === 'error' && isOffline(q.state.error)),
  );
}

/** Whether to show Foodpanda at all: enabled for the shop, switched on, and the user may use it. */
export function useFoodpanda() {
  const { status: authStatus, can } = useAuth();
  const allowed = authStatus === 'signedIn' && (can('foodpanda.orders') || can('foodpanda.manage'));
  const status = useFoodpandaStatus(allowed);
  const plugin = status.data?.plugin;
  const live = !!plugin && plugin.state !== 'NOT_ENABLED';
  return {
    status: status.data,
    loading: status.isLoading,
    /** Licensed for the shop (the setup screen shows even while the shop switch is off). */
    licensed: allowed && live,
    visible: allowed && live && plugin.shopEnabled,
    canManage: can('foodpanda.manage'),
    canOrder: can('foodpanda.orders'),
  };
}

/** Open orders refresh every few seconds: new orders must be seen quickly. */
export function useFoodpandaOrders(query: { status?: string; q?: string; page?: number }) {
  return useQuery({
    queryKey: foodpandaKeys.orders(query),
    queryFn: () =>
      unwrap(api.GET('/foodpanda/orders', { params: { query: { pageSize: 100, ...query } } })),
    refetchInterval: 10_000,
    placeholderData: (prev) => prev,
  });
}

export function useFoodpandaEvents(orderId?: string) {
  return useQuery({
    queryKey: foodpandaKeys.events(orderId),
    queryFn: () =>
      unwrap(api.GET('/foodpanda/events', { params: { query: { orderId, limit: 100 } } })),
  });
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['foodpanda'] });
}

export function useFoodpandaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFoodpanda) => unwrap(api.PATCH('/foodpanda', { body })),
    onSuccess: (data) => qc.setQueryData(foodpandaKeys.status, data),
  });
}

export function useOutletActions() {
  const refresh = useRefresh();
  return {
    create: useMutation({
      mutationFn: (body: Schemas['CreateFoodpandaOutletRequest']) =>
        unwrap(api.POST('/foodpanda/outlets', { body })),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, ...body }: Schemas['UpdateFoodpandaOutletRequest'] & { id: string }) =>
        unwrap(api.PATCH('/foodpanda/outlets/{id}', { params: { path: { id } }, body })),
      onSuccess: refresh,
    }),
  };
}

export function useOrderActions() {
  const refresh = useRefresh();
  const path = (id: string) => ({ params: { path: { id } } });
  const opts = { onSettled: refresh };
  return {
    accept: useMutation({
      mutationFn: (v: { id: string; version: number; prepMinutes?: number }) =>
        unwrap(
          api.POST('/foodpanda/orders/{id}/accept', {
            ...path(v.id),
            body: { version: v.version, prepMinutes: v.prepMinutes },
          }),
        ),
      ...opts,
    }),
    reject: useMutation({
      mutationFn: (v: { id: string; version: number; reason: RejectReason; message?: string }) =>
        unwrap(
          api.POST('/foodpanda/orders/{id}/reject', {
            ...path(v.id),
            body: { version: v.version, reason: v.reason, message: v.message ?? null },
          }),
        ),
      ...opts,
    }),
    ready: useMutation({
      mutationFn: (v: { id: string; version: number }) =>
        unwrap(
          api.POST('/foodpanda/orders/{id}/ready', { ...path(v.id), body: { version: v.version } }),
        ),
      ...opts,
    }),
    pickedUp: useMutation({
      mutationFn: (v: { id: string; version: number }) =>
        unwrap(
          api.POST('/foodpanda/orders/{id}/picked-up', {
            ...path(v.id),
            body: { version: v.version },
          }),
        ),
      ...opts,
    }),
    complete: useMutation({
      mutationFn: (v: { id: string }) =>
        unwrap(api.POST('/foodpanda/orders/{id}/complete', path(v.id))),
      ...opts,
    }),
    simulateCancel: useMutation({
      mutationFn: (v: { id: string }) =>
        unwrap(api.POST('/foodpanda/simulate/orders/{id}/cancel', { ...path(v.id), body: {} })),
      ...opts,
    }),
  };
}

export function useRefundOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['RefundFoodpandaOrderRequest'] & { id: string }) =>
      unwrap(api.POST('/foodpanda/orders/{id}/refund', { params: { path: { id } }, body })),
    onSettled: () => qc.invalidateQueries({ queryKey: ['foodpanda'] }),
  });
}

/** The menu and each outlet's item availability; refreshed now and then (stock moves). */
export function useFoodpandaMenu() {
  return useQuery({
    queryKey: foodpandaKeys.menu,
    queryFn: () => unwrap(api.GET('/foodpanda/menu')),
    refetchInterval: 60_000,
  });
}

export function useMenuActions() {
  const qc = useQueryClient();
  const put = (menu: FoodpandaMenu) => qc.setQueryData(foodpandaKeys.menu, menu);
  return {
    add: useMutation({
      mutationFn: (variantIds: string[]) =>
        unwrap(api.POST('/foodpanda/menu/items', { body: { variantIds } })),
      onSuccess: put,
    }),
    price: useMutation({
      mutationFn: (v: { id: string; version: number; price: string | null }) =>
        unwrap(
          api.PATCH('/foodpanda/menu/items/{id}', {
            params: { path: { id: v.id } },
            body: { version: v.version, price: v.price },
          }),
        ),
      onSuccess: put,
    }),
    remove: useMutation({
      mutationFn: (id: string) =>
        unwrap(api.DELETE('/foodpanda/menu/items/{id}', { params: { path: { id } } })),
      onSettled: () => qc.invalidateQueries({ queryKey: foodpandaKeys.menu }),
    }),
    push: useMutation({
      mutationFn: () => unwrap(api.POST('/foodpanda/menu/push')),
      onSuccess: put,
      onSettled: () => qc.invalidateQueries({ queryKey: foodpandaKeys.menu }),
    }),
    setAvailable: useMutation({
      mutationFn: (v: { outletId: string; itemId: string; available: boolean }) =>
        unwrap(
          api.PUT('/foodpanda/outlets/{id}/items/{itemId}', {
            params: { path: { id: v.outletId, itemId: v.itemId } },
            body: { available: v.available },
          }),
        ),
      onSuccess: put,
      onSettled: () => qc.invalidateQueries({ queryKey: foodpandaKeys.menu }),
    }),
    sync: useMutation({
      mutationFn: (outletId: string) =>
        unwrap(api.POST('/foodpanda/outlets/{id}/sync', { params: { path: { id: outletId } } })),
      onSettled: () => qc.invalidateQueries({ queryKey: ['foodpanda'] }),
    }),
  };
}

export function useOutletState() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['SetFoodpandaOutletStateRequest'] & { id: string }) =>
      unwrap(api.POST('/foodpanda/outlets/{id}/state', { params: { path: { id } }, body })),
    onSettled: refresh,
  });
}

// --- payouts and reports (FP5) ------------------------------------------------------------------

const CONTENT_TYPES: Record<string, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
};

/** Reads a payout statement; `columns` overrides the detected file columns. */
export function readStatement(
  file: File,
  columns: Partial<Record<keyof PayoutColumns, string>> = {},
) {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return unwrap(
    api.POST('/foodpanda/payouts/read', {
      params: { query: columns },
      // The API reads the raw file bytes.
      body: file as unknown as string,
      bodySerializer: (b: unknown) => b as BodyInit,
      headers: { 'Content-Type': CONTENT_TYPES[ext] ?? 'application/octet-stream' },
    }),
  );
}

export function useFoodpandaPayouts() {
  return useQuery({
    queryKey: foodpandaKeys.payouts,
    queryFn: () => unwrap(api.GET('/foodpanda/payouts')),
  });
}

export function useFoodpandaPayout(id: string | null) {
  return useQuery({
    queryKey: foodpandaKeys.payout(id ?? ''),
    queryFn: () =>
      unwrap(api.GET('/foodpanda/payouts/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: !!id,
  });
}

export function usePayoutActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: foodpandaKeys.payouts });
  const path = (id: string) => ({ params: { path: { id } } });
  return {
    create: useMutation({
      mutationFn: (body: Schemas['CreateFoodpandaPayoutRequest']) =>
        unwrap(api.POST('/foodpanda/payouts', { body })),
      onSettled: refresh,
    }),
    post: useMutation({
      mutationFn: (v: { id: string; version: number }) =>
        unwrap(
          api.POST('/foodpanda/payouts/{id}/post', { ...path(v.id), body: { version: v.version } }),
        ),
      onSettled: refresh,
    }),
    void: useMutation({
      mutationFn: (v: { id: string; version: number; reason: string }) =>
        unwrap(
          api.POST('/foodpanda/payouts/{id}/void', {
            ...path(v.id),
            body: { version: v.version, reason: v.reason },
          }),
        ),
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => unwrap(api.DELETE('/foodpanda/payouts/{id}', path(id))),
      onSettled: refresh,
    }),
  };
}

export function useFoodpandaSummary(range: { from: string; to: string }) {
  return useQuery({
    queryKey: foodpandaKeys.report('summary', range),
    queryFn: () => unwrapJson(api.GET('/reports/foodpanda/summary', { params: { query: range } })),
  });
}

export function useFoodpandaProfit(range: { from: string; to: string }, enabled: boolean) {
  return useQuery({
    queryKey: foodpandaKeys.report('profit', range),
    queryFn: () => unwrapJson(api.GET('/reports/foodpanda/profit', { params: { query: range } })),
    enabled,
  });
}

export function useSimulateOrder() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (body: Schemas['SimulateFoodpandaOrderRequest']) =>
      unwrap(api.POST('/foodpanda/simulate/orders', { body })),
    onSettled: refresh,
  });
}

/** Foodpanda's reject reasons, in words. */
export const REJECT_REASONS: { value: RejectReason; label: string }[] = [
  { value: 'TOO_BUSY', label: 'Too busy' },
  { value: 'ITEM_UNAVAILABLE', label: 'An item is not available' },
  { value: 'CLOSED', label: 'The shop is closed' },
  { value: 'TECHNICAL_PROBLEM', label: 'Technical problem' },
  { value: 'NO_PICKER', label: 'Nobody to prepare the order' },
  { value: 'FOOD_QUALITY_SPOILAGE', label: 'Food quality problem' },
  { value: 'ADDRESS_INCOMPLETE_MISSTATED', label: 'Address incomplete or wrong' },
  { value: 'OUTSIDE_SERVICE_AREA', label: 'Outside the delivery area' },
  { value: 'CONTENT_WRONG_MISLEADING', label: 'Menu content wrong' },
  { value: 'UNREACHABLE', label: 'Customer unreachable' },
  { value: 'BLACKLISTED', label: 'Customer blocked' },
  { value: 'WILL_NOT_WORK_WITH_PLATFORM', label: 'Stopping work with Foodpanda' },
];

/** Foodpanda's brand pink, for the badge and accents (never for text on white). */
export const FOODPANDA_PINK = '#D70F64';
